import { Separator } from '@talentmesh/ui/components/separator';
import { useMemo } from 'react';
import AdvancedFilterButton from '../../../../Overview/Components/Filter/AdvancedFilterButton';
import { useIdentifiedProfileFilters } from '../../../api/useIdentifiedProfiles';
import { useIdentifiedListContext } from '../utils/IdentifiedListContext';
import MenuListItemGroup from './MenuListItemGroup';
import BaseFilter from '@/Pages/Candidates/Overview/Components/Filter/BaseFilter';
import BaseFilterPanelContent from '@/Pages/Candidates/Overview/Components/Filter/BaseFilterPanelContent';
import UIStrings from '@/Utils/UIStrings';

const CountryFilter = () => {
    const { options, setOptions } = useIdentifiedListContext();
    const { data } = useIdentifiedProfileFilters();
    const resetCallback = () => setOptions((p) => ({ ...p, country: undefined, regionPair: undefined }));

    const countryMenuItems = useMemo<Parameters<typeof AdvancedFilterButton>['0']['menuItems']>(
        () =>
            data?.countries.map(({ id, name, count }) => ({
                id,
                selected: !!options.country?.find((selectedCountry) => selectedCountry === id),
                changeCallback: () => {
                    const foundIndex = options.country?.findIndex((selectedCountry) => selectedCountry === id);

                    if (typeof foundIndex === 'number' && foundIndex > -1) {
                        setOptions((p) => {
                            const country = p.country?.toSpliced(foundIndex, 1);
                            return {
                                ...p,
                                country: country?.length ? country : undefined,
                            };
                        });
                    } else {
                        setOptions((p) => ({
                            ...p,
                            country: p.country?.toSpliced(0, 0, id) || [id],
                        }));
                    }
                },
                menuItemLabel: `${name} (${count})`,
            })) || [],
        [data, options, setOptions],
    );

    const regionMenuItems = useMemo<Parameters<typeof AdvancedFilterButton>['0']['menuItems']>(
        () =>
            data?.regions.map(({ id, name, count, country, region }) => ({
                id,
                selected: !!options.regionPair?.find(
                    (selectedRegion) => selectedRegion.country === country && selectedRegion.region === region,
                ),
                changeCallback: () => {
                    const foundIndex = options.regionPair?.findIndex(
                        (selectedRegion) => selectedRegion.country === country && selectedRegion.region === region,
                    );

                    if (typeof foundIndex === 'number' && foundIndex > -1) {
                        setOptions((p) => {
                            const regionPair = p.regionPair?.toSpliced(foundIndex, 1);
                            return {
                                ...p,
                                regionPair: regionPair?.length ? regionPair : undefined,
                            };
                        });
                    } else {
                        setOptions((p) => ({
                            ...p,
                            regionPair: p.regionPair?.toSpliced(0, 0, { country, region }) || [{ country, region }],
                        }));
                    }
                },
                menuItemLabel: `${name} (${count})`,
            })) || [],
        [data, options, setOptions],
    );

    const activeCountryFilterCount = countryMenuItems.filter((item) => item.selected === true).length;
    const activeRegionFilterCount = regionMenuItems.filter((item) => item.selected === true).length;
    const activeFilterCount = activeCountryFilterCount + activeRegionFilterCount;

    const appendActiveFilterCount = (label: string, selectedCount: number) => {
        return `${label} ${selectedCount > 0 ? `(+${selectedCount})` : ''}`;
    };

    return (
        <BaseFilter label={UIStrings.Location} activeFilterCount={activeFilterCount}>
            <BaseFilterPanelContent resetCallback={resetCallback}>
                <div className="flex flex-col">
                    {countryMenuItems.length > 0 && (
                        <MenuListItemGroup
                            label={appendActiveFilterCount(UIStrings.Country, activeCountryFilterCount)}
                            items={countryMenuItems}
                        />
                    )}
                    {regionMenuItems.length > 0 && (
                        <>
                            <Separator />
                            <MenuListItemGroup
                                label={appendActiveFilterCount(UIStrings.Region, activeRegionFilterCount)}
                                items={regionMenuItems}
                            />
                        </>
                    )}
                </div>
            </BaseFilterPanelContent>
        </BaseFilter>
    );
};

export default CountryFilter;
